import { LoggerService } from '@angular-ru/cdk/logger';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DSComponentElement, DSComponentModule, DSErrorDetail } from '@digitaservice/component-types';
import { DigitaServiceError } from '@digitaservice/utils';
import { EMPTY, Subscription, asyncScheduler, catchError, filter, switchMap, tap, throttleTime } from 'rxjs';
import { IElement, IElementHeartbeatOptions } from 'src/app/api/modules/core/dynamic/element/components/IElement';
import { DIGITASERVICE_BINARY_BACKOFF } from 'src/app/app-constants';
import { selectFailureCopy } from 'src/app/app-dictionary';
import { createDigitaServiceError } from 'src/app/app-error';
import { AppRepository } from '../../../services/application/application.repository';
import { AppService } from '../../../services/application/application.service';
import { DictionaryRepository } from '../../../services/dictionary/dictionary.repository';
import { elementFailureError, reportElementFailure, reportElementLoadFailure } from './element-error';
import { ElementService } from './element.service';

/**
 * The validated, runtime-ready projection of an {@link IElement} config.
 */
interface ParsedElementConfig {
  src: string;
  configUrl: string;
  heartbeat?: IElementHeartbeatOptions;
  onReadyAPI?: string;
  onErrorAPI?: string;
  onEventAPI?: string;
  onCompleteAPI?: string;
  ariaLoadingLabel?: string;
  ariaErrorLabel?: string;
}

/**
 * Pause before the next element-load attempt.
 *
 * @param ms - how long to wait
 */
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Build the specifier for a retried bundle import.
 *
 * Re-importing the original URL would not go back to the network. A module script that fails to
 * load is recorded in the browser's module map as failed, and every later import of that same
 * specifier is handed the same failure without a new request — so changing the specifier is what
 * makes a retry actually retry. The query string does not disturb how the bundle resolves its own
 * sibling assets: a relative URL resolved against `https://host/path/index.js?retry=1` drops the
 * query and keeps the path.
 *
 * @param src - the bundle URL from the server config
 * @param retryCount - which retry this is, so each attempt is a distinct specifier
 */
const retrySpecifier = (src: string, retryCount: number): string => {
  const url = new URL(src, location.href);
  url.searchParams.set('retry', String(retryCount));
  return url.toString();
};

/**
 * Run `attempt` until it resolves, the retry budget is spent, or the caller aborts.
 *
 * Retrying every rejection is the right rule here rather than a shortcut, because of what each
 * half of an element load rejects for. `fetch` rejects only when no response arrived at all —
 * a 404 or a 403 *resolves*, and is the CDN's answer rather than a failure to reach it, so it
 * stays terminal via the caller's `ok` check. A rejected `import` means the bundle did not load.
 * Neither can reject for a reason a caller would want to distinguish here.
 *
 * The budget is {@link DIGITASERVICE_BINARY_BACKOFF} rather than the API's. The API retries ten
 * times because losing a call there kills the app, so any delay beats dying; this runs behind a
 * spinner the participant is watching, where ten attempts would just be a very long spinner.
 *
 * @param attempt - the load to run, given the current retry number
 * @param isAborted - whether the caller has gone away and the result is no longer wanted
 */
const retryElementLoad = async <T>(attempt: (retryCount: number) => Promise<T>, isAborted: () => boolean): Promise<T> => {
  for (let retryCount = 0; ; retryCount++) {
    try {
      return await attempt(retryCount);
    } catch (error) {
      if (retryCount >= DIGITASERVICE_BINARY_BACKOFF.MAX_RETRIES || isAborted()) {
        throw error;
      }
      // Exponential and capped, so the waits run 500ms, 1s, 2s.
      await wait(Math.min(DIGITASERVICE_BINARY_BACKOFF.EXPO * 2 ** retryCount, DIGITASERVICE_BINARY_BACKOFF.MAX_INTERVAL));
      // The wait outlives a destroy, so re-check before spending another attempt. The caller's own
      // destroyed-guard turns this rethrow into a silent return rather than a reported failure.
      if (isAborted()) {
        throw error;
      }
    }
  }
};

/**
 * Validate an {@link IElement} and project it into the values the component
 * needs at runtime. Throws (as the store's `applyConfiguration` did) when a
 * required field is missing, so a malformed server config fails loudly.
 *
 * @param configuration - the incoming server JSON
 */
function parseElementConfig(configuration: IElement): ParsedElementConfig {
  if (!configuration) {
    throw createDigitaServiceError(`Element`, `initialize`, `No configuration provided but this is required.`, `config`);
  }
  if (!configuration.src) {
    throw createDigitaServiceError(`Element`, `initialize`, `No "src" property provided but this is required.`, `config`);
  }
  if (!configuration.configUrl) {
    throw createDigitaServiceError(`Element`, `initialize`, `No "configUrl" property provided but this is required.`, `config`);
  }

  let heartbeat: IElementHeartbeatOptions | undefined = undefined;
  if (configuration.heartbeat) {
    const heartbeatAPI = configuration.heartbeat.api;
    if (!heartbeatAPI || heartbeatAPI.length === 0) {
      throw createDigitaServiceError(`Element`, `initialize`, `The heartbeat object was set but no heartbeat.api was provided.`, `config`);
    }

    // a throttle that is unset or below 1000ms is clamped to 1000ms
    let throttleTimeMS = configuration.heartbeat.throttleTimeMS;
    if (isNaN(throttleTimeMS) || throttleTimeMS < 1000) {
      throttleTimeMS = 1000;
    }

    heartbeat = { api: heartbeatAPI, throttleTimeMS };
  }

  return {
    src: configuration.src,
    configUrl: configuration.configUrl,
    heartbeat,
    onReadyAPI: configuration.onReadyAPI || undefined,
    onErrorAPI: configuration.onErrorAPI || undefined,
    onEventAPI: configuration.onEventAPI || undefined,
    onCompleteAPI: configuration.onCompleteAPI || undefined,
    ariaLoadingLabel: configuration.ariaLoadingLabel,
    ariaErrorLabel: configuration.ariaErrorLabel,
  };
}

/**
 * Loads and hosts a dynamically-imported custom (Lit) element — the bridge
 * between server JSON and an interactive game/widget bundle.
 *
 * Flow: fetch the resolved config JSON and `import()` the JS bundle in parallel,
 * create the custom element outside Angular's zone (so rAF/Three.js render loops
 * don't trip change detection), wire its CustomEvents back into the zone, and
 * relay the `complete`/`error`/`event`/`ready`/`heartbeat` lifecycle to the
 * server APIs the config names.
 */
@Component({
  selector: 'app-element',
  templateUrl: './element.component.html',
  styleUrls: ['./element.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatProgressSpinner],
  host: {
    '[class.widget-auto]': 'isWidgetAuto()',
  },
})
export class ElementComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly appRepository = inject(AppRepository);
  private readonly appService = inject(AppService);
  private readonly ngZone = inject(NgZone);
  private readonly loggerService = inject(LoggerService);
  private readonly api = inject(ElementService);
  private readonly dictionaryRepository = inject(DictionaryRepository);

  /**
   * Configures the Element component with server JSON. Set by the dynamic
   * factory via `setInput`.
   */
  readonly config = input.required<IElement>();

  /**
   * The host container where the custom element is appended.
   */
  private readonly elementHost = viewChild.required<ElementRef<HTMLDivElement>>('elementHost');

  /**
   * The validated, runtime-ready config — parsed once in {@link ngOnInit} so a
   * malformed config fails loudly at a single deterministic point, not lazily
   * from a template binding. Lifecycle + element callbacks read this.
   */
  private parsedConfig!: ParsedElementConfig;

  ///////////////////////////////////////////////////////////////////////////
  // TEMPLATE-FACING STATE
  ///////////////////////////////////////////////////////////////////////////

  /** Has the custom element dispatched its `ready` event? Drives the reveal. */
  protected readonly ready = signal(false);

  /**
   * Has the element failed with nothing downstream to handle it?
   *
   * Set for a bundle or config that never loaded, and for an element that reported its own fatal
   * error with no onErrorAPI configured to answer it. An element error that does have one is
   * deliberately excluded: that path ends at the app's own fatal screen, and a message here would
   * only occupy the gap.
   */
  protected readonly failed = signal(false);

  /** The message shown to the player when the element has failed. */
  protected readonly failureMessage = signal('');

  /** Is interaction with the element currently enabled? */
  protected readonly interactionEnabled = signal(false);

  /**
   * Is the app running inside a widget-auto iframe? Drives the `widget-auto` host
   * class, which is what the stylesheet keys its `aspect-ratio` sizing off.
   *
   * The class is applied by this component rather than read from an ancestor so the
   * sizing rules — including their breakpoint — stay in this component's own
   * stylesheet. The widget mode is resolved during app startup, before any screen
   * renders, and the store emits synchronously, so the class is present on the
   * first change-detection pass and there is no flash at the wrong height.
   */
  protected readonly isWidgetAuto = toSignal(this.appRepository.shouldApplyWidgetAutoLayout$, { initialValue: false });

  /** Accessible label for the loading spinner. Read straight from the raw config
   * so it never depends on the (throwing) full-config validation. */
  protected readonly ariaLoadingLabel = computed(() => this.config().ariaLoadingLabel || 'Loading');

  /** Accessible label for the error container. Read straight from the raw config
   * so it never depends on the (throwing) full-config validation. */
  protected readonly ariaErrorLabel = computed(() => this.config().ariaErrorLabel || 'Error');

  ///////////////////////////////////////////////////////////////////////////
  // INTERNAL STATE
  ///////////////////////////////////////////////////////////////////////////

  /** Latest data from the element; throttled out via the heartbeat stream. */
  private readonly heartbeatData = signal<Record<string, unknown> | undefined>(undefined);

  /** Source stream for the heartbeat (created here, an injection context). */
  private readonly heartbeatData$ = toObservable(this.heartbeatData);

  /** The last payload handed to the heartbeat API, so the final flush can tell whether the
   * newest data has already been reported. */
  private lastHeartbeatDataSent?: Record<string, unknown>;

  /** Reference to the created custom element for cleanup. */
  private litElement?: DSComponentElement;

  /** The custom element's tag, once the bundle has been imported. Identifies the element in a
   * failure report, which is the difference between "a game broke" and "this game broke". */
  private componentTag?: string;

  /** Event-listener cleanup functions for the custom element. */
  private eventCleanupFns: (() => void)[] = [];

  /** Prevents async element creation from acting after destruction. */
  private destroyed = false;

  // Subscriptions that need explicit cancellation (cancel-previous, or
  // cancel-on-complete for the heartbeat).
  private heartbeatSubscription?: Subscription;
  private heartbeatFlushSubscription?: Subscription;
  private onReadyAPISubscription?: Subscription;
  private onCompleteAPISubscription?: Subscription;
  private onErrorAPISubscription?: Subscription;
  private onEventAPISubscription?: Subscription;

  // Handles for the deferred element load — cancelled on destroy.
  private loadRafHandle?: number;
  private loadTimeoutHandle?: ReturnType<typeof setTimeout>;

  /////////////////////////////////////////////////////////////////////////
  // LIFE CYCLE
  /////////////////////////////////////////////////////////////////////////

  ngOnInit() {
    // Validate + project the server config once, up front, so a malformed
    // config fails loudly at a single deterministic point.
    this.parsedConfig = parseElementConfig(this.config());
  }

  ngAfterViewInit() {
    const { src, configUrl, heartbeat } = this.parsedConfig;

    // Defer the element load until the loading spinner has painted and
    // animated into view. Without this, the bundle fetch can saturate the
    // main thread before the browser paints the spinner — on mobile the
    // spinner never appears at all.
    this.loadRafHandle = requestAnimationFrame(() => {
      this.loadTimeoutHandle = setTimeout(() => {
        this.loadAndCreateElement(src, configUrl);
      }, 300);
    });

    // The heartbeat periodically sends the latest element data to the server,
    // throttled per the config.
    if (heartbeat) {
      this.heartbeatSubscription = this.heartbeatData$
        .pipe(
          // Filter BEFORE throttling. `heartbeatData` starts undefined and emits that seed
          // as soon as anything subscribes, so throttling first lets the seed open the
          // throttle window and swallow the element's first real heartbeat.
          filter((data): data is Record<string, unknown> => !!data),
          // `trailing` matters as much as `leading`: the first beat goes out immediately,
          // and the newest data seen during a window is sent when that window closes, so
          // data that stops changing is still reported rather than discarded. The trailing
          // emission restarts the window, so this is still at most one request per period.
          throttleTime(heartbeat.throttleTimeMS, asyncScheduler, { leading: true, trailing: true }),
          tap((data) => (this.lastHeartbeatDataSent = data)),
          // catchError inside switchMap drops a failed beat but keeps the stream
          // alive — a terminal error here would otherwise kill the heartbeat for
          // the rest of the session. Fire-and-forget telemetry.
          switchMap((data) => this.api.requestOnHeartbeatAPI(heartbeat.api, data).pipe(catchError(() => EMPTY))),
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;

    // Cancel the deferred element-load timers (the destroyed-guard also stops
    // loadAndCreateElement from acting, but don't leave a timer pending).
    if (this.loadRafHandle !== undefined) {
      cancelAnimationFrame(this.loadRafHandle);
    }
    clearTimeout(this.loadTimeoutHandle);

    // Remove all event listeners from the custom element
    this.eventCleanupFns.forEach((fn) => fn());
    this.eventCleanupFns = [];

    // Remove the element from the DOM
    if (this.litElement && this.litElement.parentNode) {
      this.litElement.parentNode.removeChild(this.litElement);
    }
    this.litElement = undefined;

    // Cancel the explicitly-tracked subscriptions
    this.heartbeatSubscription?.unsubscribe();
    this.heartbeatFlushSubscription?.unsubscribe();
    this.onReadyAPISubscription?.unsubscribe();
    this.onCompleteAPISubscription?.unsubscribe();
    this.onErrorAPISubscription?.unsubscribe();
    this.onEventAPISubscription?.unsubscribe();
  }

  /////////////////////////////////////////////////////////////////////////
  // ELEMENT CREATION
  /////////////////////////////////////////////////////////////////////////

  /**
   * Fetch the element's config JSON, retrying a request that never arrived.
   *
   * The response is handed back whatever its status: a 404 or a 403 is the CDN answering, and
   * {@link loadAndCreateElement} reports it. Only a request that got no response at all is retried.
   *
   * @param configUrl - the URL of the resolved config JSON
   */
  private fetchConfig(configUrl: string): Promise<Response> {
    return retryElementLoad(
      () => fetch(configUrl),
      () => this.destroyed,
    );
  }

  /**
   * Import the element's JS bundle, retrying against a specifier the browser has not already failed.
   *
   * The first attempt uses the URL as the server gave it, so the ordinary path keeps normal HTTP
   * caching; only retries carry the marker.
   *
   * @param src - the URL of the JS bundle to import
   */
  private importBundle(src: string): Promise<DSComponentModule> {
    return retryElementLoad(
      (retryCount) =>
        import(/* @vite-ignore */ retryCount === 0 ? src : retrySpecifier(src, retryCount)) as unknown as Promise<DSComponentModule>,
      () => this.destroyed,
    );
  }

  /**
   * Dynamically import the JS bundle and create the custom element.
   *
   * @param src - the URL of the JS bundle to import
   * @param configUrl - the URL of the resolved config JSON
   */
  private async loadAndCreateElement(src: string, configUrl: string) {
    this.loggerService.log('[Element] loading bundle', src);
    this.loggerService.log('[Element] fetching config', configUrl);

    try {
      // Fetch the config JSON and import the JS bundle in parallel. Each half retries on its own,
      // so whichever one arrived first is not thrown away while the other is still trying.
      const [configResponse, module] = await Promise.all([this.fetchConfig(configUrl), this.importBundle(src)]);

      // Bail if the component was destroyed while loading
      if (this.destroyed) {
        return;
      }

      // Parse the config JSON
      if (!configResponse.ok) {
        throw new Error(`Failed to fetch config from ${configUrl}: ${configResponse.status} ${configResponse.statusText}`);
      }
      const resolvedConfig = (await configResponse.json()) as Record<string, unknown>;

      // Extract the custom element tag from the module
      const componentTag = module.COMPONENT_TAG;
      if (!componentTag) {
        throw new Error('Module does not export COMPONENT_TAG');
      }
      this.componentTag = componentTag;

      // Run the element outside Angular's zone so that Three.js/rAF-based
      // rendering loops don't trigger change detection on every frame.
      // Event listeners re-enter the zone individually via ngZone.run().
      this.ngZone.runOutsideAngular(() => {
        // Create the element
        const element = document.createElement(componentTag) as DSComponentElement;

        // Attach event listeners BEFORE setting config to avoid missing fast events
        this.attachEventListeners(element);

        // Set the game config from the fetched JSON
        element.config = resolvedConfig;

        // Append to the host container
        this.elementHost().nativeElement.appendChild(element);

        // Store reference for cleanup
        this.litElement = element;
      });

      this.loggerService.log('[Element] created', componentTag);
    } catch (error) {
      // Bail if the component was destroyed while loading
      if (this.destroyed) {
        return;
      }

      this.ngZone.run(() => {
        this.loggerService.error('[Element] bundle load failed', error);
        reportElementLoadFailure(src, error);
        this.fail(error);
      });
    }
  }

  /**
   * Puts the component into its failed state and chooses what the player is told.
   *
   * The copy comes from the dictionary rather than the error, because the error's own text is a
   * developer's sentence - a CDN URL, an HTTP status, the name of an asset - and none of it is
   * something a player can act on. This is the same mapping the app uses for a fatal error, so
   * the three outcomes a player can actually do something about stay worded in one place.
   *
   * @param error - whatever failed.
   */
  private fail(error: unknown) {
    this.interactionEnabled.set(false);
    this.failureMessage.set(selectFailureCopy(error, this.dictionaryRepository.getDictionary()));
    this.failed.set(true);
  }

  /**
   * Attach CustomEvent listeners to the custom element.
   *
   * All listeners are wrapped in ngZone.run() to ensure Angular change detection.
   *
   * @param element - the custom element to listen on
   */
  private attachEventListeners(element: HTMLElement) {
    const listen = (eventName: string, handler: (event: CustomEvent) => void) => {
      const wrappedHandler = (e: Event) => {
        this.ngZone.run(() => handler(e as CustomEvent));
      };
      element.addEventListener(eventName, wrappedHandler);
      this.eventCleanupFns.push(() => element.removeEventListener(eventName, wrappedHandler));
    };

    listen('ready', (event) => this.onElementReady(event));
    listen('complete', (event) => this.onElementComplete(event));
    listen('error', (event) => this.onElementError(event));
    listen('event', (event) => this.onElementEvent(event));
    listen('heartbeat', (event) => this.onElementHeartbeat(event));
  }

  ///////////////////////////////////////////////////////////////////////////
  // ELEMENT CALLBACKS
  ///////////////////////////////////////////////////////////////////////////

  /**
   * When an Element dispatches a `ready` CustomEvent, the element becomes
   * interactive and we may forward the detail to the onReadyAPI.
   *
   * @param event - the CustomEvent dispatched from the element
   */
  private onElementReady(event: CustomEvent) {
    this.loggerService.log('[Element]::onElementReady', event.detail);

    // the element is now interactive and revealed
    this.interactionEnabled.set(true);
    this.ready.set(true);

    // fire and forget any onReadyAPI call.
    this.onReadyAPISubscription?.unsubscribe();
    const onReadyAPI = this.parsedConfig.onReadyAPI;
    if (onReadyAPI) {
      this.onReadyAPISubscription = this.api.requestOnReadyAPI(onReadyAPI, event.detail ?? {}).subscribe();
    }
  }

  /**
   * When an Element dispatches a `complete` CustomEvent, the game has finished
   * and provided metrics. The server processes them and tells us where to go next.
   *
   * @param event - the CustomEvent dispatched from the element
   */
  private onElementComplete(event: CustomEvent) {
    this.loggerService.log('[Element]::onElementComplete', event.detail);

    // disable interaction on the element
    this.interactionEnabled.set(false);

    // any existing complete request and the heartbeat should be canceled.
    this.onCompleteAPISubscription?.unsubscribe();
    this.heartbeatSubscription?.unsubscribe();

    // ...but not before the data the heartbeat was still holding back is sent. Fired ahead
    // of the onCompleteAPI request so it is in flight before any navigation to the
    // destination.
    this.flushHeartbeat();

    const onCompleteAPI = this.parsedConfig.onCompleteAPI;
    if (onCompleteAPI) {
      this.onCompleteAPISubscription = this.api.requestOnCompleteAPI(onCompleteAPI, event.detail ?? {}).subscribe((response) => {
        this.loggerService.log('[Element]::onCompleteAPI success', response);

        // there should be a destination.
        if (response?.destination) {
          this.loggerService.log('[Element]::onElementComplete navigate to destination', response.destination);
          this.appService.openLink(response.destination);
        } else {
          // otherwise throw if no destination is provided.
          throw new DigitaServiceError('[Element] - Responses from the onCompleteAPI should always provide a "destination" property.');
        }
      });
    }
  }

  /**
   * When an Element dispatches an `error` CustomEvent, it has experienced
   * trouble that cannot be fixed.
   *
   * Where the player ends up depends on whether the config named an onErrorAPI, and the two
   * outcomes are deliberately not the same.
   *
   * With one, the failure is the server's to answer, and answering it is what ends the
   * experience: the endpoint replies with an `error` payload, which every response passes
   * through the API service's error path, so the reply is thrown, reaches the global handler and
   * takes the whole app to its fatal screen. Showing a message here first would put wording on
   * screen for the length of one request and then destroy it, so this component shows nothing
   * and lets that run.
   *
   * Without one, nothing downstream is listening. This component is the last thing that knows
   * the element is dead, so it says so rather than leaving a game on screen that can no longer
   * be played, with pointer events switched off and no explanation.
   *
   * @param event - the CustomEvent dispatched from the element
   */
  private onElementError(event: CustomEvent) {
    this.loggerService.log('[Element]::onElementError', event.detail);

    // Reported here rather than left to whatever the onErrorAPI round trip raises. That reply is
    // the element's own message with the player's browser and OS appended, thrown from the one
    // place every endpoint is handled, so it arrives with no element frames and a value that
    // differs per device. This report has the element's stack and groups by what broke.
    const detail = event.detail as DSErrorDetail | undefined;
    if (detail?.message) {
      reportElementFailure(this.componentTag ?? 'unknown', detail);
    }

    // interaction is over whichever way this goes
    this.interactionEnabled.set(false);

    // any existing error request should be canceled.
    this.onErrorAPISubscription?.unsubscribe();

    const onErrorAPI = this.parsedConfig.onErrorAPI;
    if (!onErrorAPI) {
      // The detail is a plain object crossing an event boundary, so it is rebuilt into an Error
      // before the copy is chosen - that is the shape the failure-to-wording mapping reads.
      this.fail(detail?.message ? elementFailureError(detail) : detail);
      return;
    }

    this.onErrorAPISubscription = this.api.requestOnErrorAPI(onErrorAPI, event.detail ?? {}).subscribe((response) => {
      // The endpoint the server currently names for this answers with an error and never a
      // destination, so this never runs against it today. Kept because onErrorAPI is a
      // configurable path and a future one may well redirect instead of ending the session.
      if (response?.destination) {
        this.appService.openLink(response.destination);
      }
    });
  }

  /**
   * When an Element dispatches an `event` CustomEvent, it is routed to the
   * onEventAPI.
   *
   * @param event - the CustomEvent dispatched from the element
   */
  private onElementEvent(event: CustomEvent) {
    this.loggerService.log('[Element]::onElementEvent', event.detail);

    // any existing event request should be canceled.
    this.onEventAPISubscription?.unsubscribe();

    const onEventAPI = this.parsedConfig.onEventAPI;
    if (onEventAPI) {
      this.onEventAPISubscription = this.api.requestOnEventAPI(onEventAPI, event.detail ?? {}).subscribe();
    }
  }

  /**
   * When an Element dispatches a `heartbeat` CustomEvent, the data is stored
   * for the throttled heartbeat stream.
   *
   * @param event - the CustomEvent dispatched from the element
   */
  private onElementHeartbeat(event: CustomEvent) {
    // update the heartbeat data if there is detail
    if (event.detail) {
      this.heartbeatData.set(event.detail);
    }
  }

  /**
   * Send one last heartbeat with the newest data, unless the throttle already sent it.
   *
   * The throttle exists to keep a fast-beating element from flooding the server, which means
   * the newest data is routinely still pending when the element completes. Without this
   * flush a short session reports nothing at all.
   */
  private flushHeartbeat() {
    const heartbeat = this.parsedConfig.heartbeat;
    const data = this.heartbeatData();
    if (!heartbeat || !data || data === this.lastHeartbeatDataSent) {
      return;
    }

    this.lastHeartbeatDataSent = data;
    this.heartbeatFlushSubscription?.unsubscribe();
    this.heartbeatFlushSubscription = this.api
      .requestOnHeartbeatAPI(heartbeat.api, data)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }
}
